import { Box, Button, Link, Text, VStack } from '@chakra-ui/react';
import {
  MAX_COLLECTION_PRODUCTS,
  type BriefCompensation,
  type BriefCatalogProduct,
} from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GiftSelectionLimit } from '@/components/gifts/GiftSelectionLimit';
import { ProductSelector } from '@/components/flows/FlowEditor/properties/ProductSelector';
import { BriefProductPicker, toBriefCatalogProduct } from './BriefProductPicker';
import type { MoneyAmountInputVariant } from '../editor/sections/MoneyAmountField';

type GiftOffer = BriefCompensation['giftedProduct'];

/** The same catalog cards and value controls as a single gift, with an explicit choice ceiling. */
export function BriefGiftOfferPicker({
  value,
  onChange,
  amountInputVariant,
}: {
  value: GiftOffer;
  onChange: (value: GiftOffer) => void;
  amountInputVariant?: MoneyAmountInputVariant;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const products =
    value?.source === 'catalog'
      ? (value.catalogProducts ?? (value.catalog ? [value.catalog] : []))
      : [];
  const maximum = value?.maxSelectableProducts ?? 1;
  const setProducts = (next: BriefCatalogProduct[]) =>
    onChange(
      next.length
        ? {
            source: 'catalog',
            catalog: next[0],
            manual: null,
            ...(next.length > 1
              ? {
                  catalogProducts: next,
                  maxSelectableProducts: Math.min(maximum, next.length),
                }
              : {}),
          }
        : null
    );

  if (!products.length) {
    return (
      <BriefProductPicker
        amountInputVariant={amountInputVariant}
        value={
          value ? { catalog: value.catalog ?? undefined, manual: value.manual ?? undefined } : null
        }
        onChange={(product) =>
          onChange(
            product
              ? {
                  source: product.catalog ? 'catalog' : 'manual',
                  catalog: product.catalog ?? null,
                  manual: product.manual ?? null,
                }
              : null
          )
        }
        variableStyle
        withValue
      />
    );
  }

  return (
    <VStack align="stretch" gap="2.5">
      {products.map((product, index) => (
        <BriefProductPicker
          amountInputVariant={amountInputVariant}
          key={product.shopifyProductId}
          value={{ catalog: product }}
          onChange={(next) =>
            setProducts(
              next?.catalog
                ? products.map((item, position) => (position === index ? next.catalog! : item))
                : products.filter((_, position) => position !== index)
            )
          }
          variableStyle
          withValue
          catalogOnly
        />
      ))}
      {products.length > 1 && (
        <Box>
          <GiftSelectionLimit
            productCount={products.length}
            value={maximum}
            onChange={(maxSelectableProducts) => onChange({ ...value!, maxSelectableProducts })}
          />
          <Text fontSize="11.5px" color="fg.muted" mt="1.5">
            {t('collabs.briefTemplates.product.selectionLimit', {
              limit: maximum,
              total: products.length,
            })}
          </Text>
        </Box>
      )}
      {products.length < MAX_COLLECTION_PRODUCTS &&
        (adding ? (
          <ProductSelector
            selectedProductIds={products.map((product) => product.shopifyProductId)}
            onToggle={(product) => {
              if (products.some((item) => item.shopifyProductId === product.id)) return;
              setProducts([...products, toBriefCatalogProduct(product)]);
            }}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            alignSelf="flex-start"
            onClick={() => setAdding(true)}
          >
            {t('collabs.briefTemplates.product.addOptions')}
          </Button>
        ))}
      <Link
        fontSize="11.5px"
        color="fg.muted"
        textDecoration="underline"
        onClick={() =>
          onChange({
            source: 'manual',
            catalog: null,
            manual: { name: '', valueCents: null, currency: null },
          })
        }
      >
        {t('collabs.briefTemplates.product.switchToManual')}
      </Link>
    </VStack>
  );
}
